<template>
  <AutoComplete
      dropdown
      v-model="selectedItem"
      @itemSelect='handleContentClick'
      :suggestions="filteredVariables"
      @complete="search"
      optionLabel="label"
      optionGroupLabel="label"
      optionGroupChildren="items"
      placeholder="Search Value Model"
      :disabled="disabled"
  >
    <template #optiongroup="slotProps">
      <div class="labels flex align-items-center">
        <div>{{ slotProps.option.label }}</div>
      </div>
    </template>
  </AutoComplete>
</template>

<script setup lang="ts">
import {ref, onMounted,watch} from "vue";
import {useGivingTreeStore, useMainStore, useWidgetStore} from "@/store";
import {storeToRefs} from "pinia";
import {FilterMatchMode, FilterService} from "primevue/api";

const widgetStore = useWidgetStore()
const { tableCellHookId } = storeToRefs(useMainStore())
const { selectedWidget } = storeToRefs(widgetStore)
const { globalCalculations, metaByHookId, variablesByHookId, valueDriversByHookId, priceComponentsByHookId } = storeToRefs(useGivingTreeStore())

const props = defineProps({
  refVmDataVisible: {
    type: Object
  },
  hookId: {
    type: String,
    required: false
  },
  chartData: {
    type: Boolean
  },
  disabled: {
    type: Boolean
  },
  focusedCell: {
    type: Array || null
  }
})

const selectedItem = ref()
const filteredVariables = ref()
const groupedVariables = ref([])

onMounted(() => {
  transformedData()

  if (props.hookId != undefined) {
    findItemByHookId(props.hookId)
  }
  else {
    selectedWidget.value.hookId ? findItemByHookId(selectedWidget.value.hookId) : ''
  }
})

watch(() => props.hookId, () => {
  if (props.hookId != undefined) {
    findItemByHookId(props.hookId)
  }
})

// using a watch to pre-populate autocomplete in table style panel
watch(tableCellHookId, (newVal) => {
  findItemByHookId(newVal)
})

const isEmptyObject = (obj: any) => Object.entries(obj).length === 0

const handleContentClick = (event: any) => {

  if (!props.chartData) {
    const item = event.value
    selectedWidget.value.text = item.label
    selectedWidget.value.hookId = item.hookId

    props.refVmDataVisible ? props.refVmDataVisible.hide() : ''
  }
}

const findItemByHookId = (hookId: string) => {
  selectedItem.value = null
  groupedVariables.value.forEach(category => {
    const foundItem = category.items.find(item => item.hookId === hookId);
    if (foundItem && !selectedItem.value) {
      selectedItem.value = foundItem;
    }
  })
}

const search = (event: any) => {
  const query = event.query;

  let newFilteredVariables: any = [];

  groupedVariables.value.forEach((group) => {
    const filteredItems = FilterService.filter(group.items, ['label'], query, FilterMatchMode.CONTAINS);
    if (filteredItems && filteredItems.length) {
      newFilteredVariables.push({ ...group, ...{ items: filteredItems } });
    }
  });

  filteredVariables.value = newFilteredVariables;
}

const transformedData = () => {

  const meta = metaByHookId.value
  const variables = variablesByHookId.value
  const valueDrivers = valueDriversByHookId.value
  const priceComponents = priceComponentsByHookId.value
  const metaHookIds = Object.keys(meta);
  const variableHookIds = Object.keys(variables);
  const valueDriverHookIds = Object.keys(valueDrivers);
  const priceComponentHookIds = Object.keys(priceComponents);
  const transformedDataArray = [];

  const metaArray = metaHookIds.map((metaHookId) => {
    const metaItem = meta[metaHookId].name;
    return { label: metaItem, value: metaItem, hookId: metaHookId };
  });

  const variableArray = variableHookIds.map((variableHookId) => {
    const variable = variables[variableHookId].node.variable.Name;
    return { label: variable, value: variable, hookId: variableHookId };
  });

  const valueDriverArray = valueDriverHookIds.map((valueDriverHookId) => {
    const valueDriver = valueDrivers[valueDriverHookId].node.valueDriver.Name;
    return { label: valueDriver, value: valueDriver, hookId: valueDriverHookId };
  });

  const ourPriceHooks = priceComponentHookIds.filter((hookId) => hookId.includes('cpc'));
  const compPriceHooks = priceComponentHookIds.filter((hookId) => hookId.includes('opc'));

  const ourPriceArray = ourPriceHooks.map((hookId) => {
    const ourPrice = priceComponents[hookId].node.variable.Name;
    return { label: ourPrice, value: ourPrice, hookId: hookId };
  });

  const compPriceArray = compPriceHooks.map((hookId) => {
    const compPrice = priceComponents[hookId].node.variable.Name;
    return { label: compPrice, value: compPrice, hookId: hookId };
  });

  const globalCalcsArray = Object.entries(globalCalculations.value).map(([key]) => {
    const formattedKey = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (str) => str.toUpperCase());
    return {
      label: formattedKey,
      value: formattedKey,
      hookId: key
    };
  });

  transformedDataArray.push({
    label: 'Value Model',
    items: metaArray,
  });

  transformedDataArray.push({
    label: 'Global Calculations',
    items: globalCalcsArray,
  });

  transformedDataArray.push({
    label: 'Value Drivers',
    items: valueDriverArray,
  });

  transformedDataArray.push({
    label: 'Variables',
    items: variableArray,
  });

  transformedDataArray.push({
    label: 'Our Price Components',
    items: ourPriceArray,
  });

  transformedDataArray.push({
    label: 'Competitor Price Components',
    items: compPriceArray,
  });

  groupedVariables.value = transformedDataArray;

}
</script>

<style >

.p-autocomplete-panel .p-autocomplete-items .p-autocomplete-item-group {
  background-color: rgba(190, 190, 190, 0.3);
}
</style>
